
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { formatCurrency, formatDate } from '../utils';
import { RepaymentFilters } from './repayments/RepaymentFilters';
import { RepaymentsTable } from './repayments/RepaymentsTable';
import { PaymentDetailsDialog } from './repayments/PaymentDetailsDialog';

interface RepaymentsTabProps {
  repayments: any[];
}

export const RepaymentsTab: React.FC<RepaymentsTabProps> = ({ repayments }) => {
  const [selectedRepaymentId, setSelectedRepaymentId] = useState<number | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const selectedRepayment = selectedRepaymentId 
    ? repayments.find(r => r.id === selectedRepaymentId) 
    : null;

  // Filter repayments based on period and status
  const filteredRepayments = repayments.filter(repayment => {
    // Period filter
    if (filterPeriod === "upcoming" && repayment.status !== "Due") {
      return false;
    } else if (filterPeriod === "thisMonth") {
      const now = new Date();
      const repaymentDate = new Date(repayment.date);
      return repaymentDate.getMonth() === now.getMonth() && 
             repaymentDate.getFullYear() === now.getFullYear();
    } else if (filterPeriod === "thisQuarter") {
      const now = new Date();
      const repaymentDate = new Date(repayment.date);
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const repaymentQuarter = Math.floor(repaymentDate.getMonth() / 3);
      return repaymentQuarter === currentQuarter && 
             repaymentDate.getFullYear() === now.getFullYear();
    }
    
    // Status filter
    if (filterStatus !== "all" && repayment.status !== filterStatus) {
      return false;
    }
    
    // Search query
    if (searchQuery && !repayment.borrowingName?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  const handleViewDetails = (repayment: any) => {
    setSelectedRepaymentId(repayment.id);
    setShowPaymentDialog(true);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Repayment Schedule</CardTitle>
            <CardDescription>
              Schedule of principal and interest payments
            </CardDescription>
          </div>
          <RepaymentFilters 
            filterPeriod={filterPeriod}
            filterStatus={filterStatus}
            searchQuery={searchQuery}
            onPeriodChange={setFilterPeriod}
            onStatusChange={setFilterStatus}
            onSearchChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </CardHeader>
      <CardContent>
        <RepaymentsTable 
          repayments={filteredRepayments} 
          onViewDetails={handleViewDetails}
        />
      </CardContent>
      <CardFooter className="bg-muted/50 py-3 flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Showing {filteredRepayments.length} of {repayments.length} repayments
        </div>
        <div className="flex gap-2">
          <Button size="sm">Download Schedule</Button>
          <Button variant="outline" size="sm">Process Batch Payments</Button>
        </div>
      </CardFooter>

      {/* Payment Details Dialog */}
      <PaymentDetailsDialog
        open={showPaymentDialog}
        onOpenChange={setShowPaymentDialog}
        repayment={selectedRepayment}
      />
    </Card>
  );
};
